/*
 * Simple Image
 *
 * Supports:
 * - Retina
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React, { useState, useRef } from 'react';

import { useLazyImage } from '$hooks/images';

const emptySrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
import IconGallery from '$partials/icons/icon-gallery';
/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').GallerySimpleImage}
 */
export const GallerySimpleImage = ({
    alt,
    className,
    psrc,
    px2,
    role,
    src,
    style,
    x2
}) => {
    const imageRef = useRef(null);

    const {
        imageSrc,
        imageX2,
        loaded
    } = useLazyImage({
        imageRef,
        src,
        psrc,
        x2,
        px2
    });

    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });

    const handleMouseMove = (e) => {
        const rect = imageRef.current?.getBoundingClientRect();
        if (!rect) { return; }
        // position the 40x40 box so its bottom-right corner is at the cursor
        const x = e.clientX - rect.left - 40;
        const y = e.clientY - rect.top - 40;
        setCursorPos({ x, y, visible: true });
    };

    const handleMouseLeave = () => setCursorPos((s) => ({ ...s, visible: false }));

    return (
        <div className='relative cursor-pointer '>
            <img
                alt={alt}
                className={className}
                onMouseEnter={() => setCursorPos((s) => ({ ...s, visible: true }))}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                ref={imageRef}
                role={role}
                src={imageSrc || emptySrc}
                srcSet={imageX2 ? `${imageX2} 2x` : null}
                style={{
                    ...{
                        opacity: loaded ? 1 : 0,
                        transition: 'opacity .3s'
                    },
                    ...style
                }} />
            {/* Cursor-following grey box (40x40) */}
            <div
                aria-hidden
                className='hidden md:flex absolute left-0 top-0 h-40 w-40 rounded bg-grey-grey-200 pointer-events-none items-center justify-center'
                style={{
                    transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`,
                    transition: 'transform 0.04s linear, opacity 0.08s linear',
                    opacity: cursorPos.visible ? 1 : 0,
                    zIndex: 40,
                }}>
                <IconGallery fill='#2c2b2aff'/>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default GallerySimpleImage;
